import React, { useState, useRef, useContext, useEffect } from "react";
import {
  downloadFile,
  getDocsByCategoryID,
  getNowSeason,
  deepObjectComparison,
  isObjectInList,
} from "./utilities";
import { useSelector, useDispatch } from "react-redux";
import { addCart } from "../store/reducer";
import { useNavigate } from "react-router-dom";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { renderTooltip } from "./helpful";
import { LangContext } from "../context/context";
import { LupdateSkeleton } from "./skeleton";
import { existInCart } from "./constant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { saveCart, getCart } from "../api/api";

/**
 * The BookPost component.
 *
 * This component renders a book post based on the L'Update category.
 * It fetches the documents from the server and renders the post items.
 *
 * @param {Object} props - The props object.
 * @returns {ReactElement} - The rendered BookPost component.
 * @author lionel ghogue ngondeke <lionelladrog@gmail.com> 2024/05/12
 */

export const BookPost = (props) => {
  const categories = useSelector((state) => state.user.docCategories);
  const userID = useSelector((state) => state.user.userID);
  const seasons = useSelector((state) => state.user.seasons);
  const { lang } = useContext(LangContext);
  const categoryName = "L'Update";
  const [categoryID, setCategoryID] = useState(2233);
  const queryKey = ["docs", lang, categoryName, null, null];
  const date = getNowSeason(seasons);
  const [documents, setDocuments] = useState([]);
  const { data: datas, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      getDocsByCategoryID(
        lang,
        categoryName,
        categoryID,
        categories,
        null,
        date
      ),
    enabled: !!lang && !!categoryName,
    cacheTime: 1000 * 60 * 30,
    staleTime: 1000 * 60 * 30,
  });

  const [hoveredItemId, setHoveredItemId] = useState(null);
  const updateItem = useRef([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const addedItemIncart = useSelector((state) => state.cart);
  const cart = addedItemIncart.cart;
  const [isExisting, setIsExisting] = useState(false);

  useEffect(() => {
    getNowSeason(seasons);

    if (datas) {
      setDocuments(datas[0].data);
    }
  }, [datas, seasons]);

  const handleHover = (id) => {
    setTimeout(() => {
      setHoveredItemId(id);
    }, 100);
  };

  const handleHoverOut = () => {
    setHoveredItemId(null);
  };

  const downloadItem = (e, item) => {
    e.preventDefault();

    downloadFile(item.name, item.filePath);
  };

  const addItem = async (e, item) => {
    e.preventDefault();
    const isIncluded = isObjectInList(item, cart);
    let value = {};
    value["userID"] = userID;
    value["file"] = item;

    if (isIncluded) {
      setIsExisting(true);
      return;
    } else {
      let resp = await saveCart(value);
      if (resp) {
        let userCart = await getCart(userID);

        if (userCart) {
          userCart.forEach((item) => {
            const isIncluded = isObjectInList(item, cart);

            if (isIncluded) {
              return;
            }

            dispatch(addCart(item));
          });
        }
      }
    }
  };

  const viewItem = (e) => {
    e.preventDefault();
    navigate("/l-update-viewer");
  };

  return (
    <div className="row ">
      {isLoading ? (
        <LupdateSkeleton />
      ) : (
        <>
          {documents.map((doc, index) => {
            let item = doc.data[0];
            let title = doc.title;
            const isIncluded = isObjectInList(item, cart);
            item.id = index;

            return (
              <div className="col-12 col-sm-3 my-5 " key={index}>
                <div
                  className="post-item "
                  key={item.name + Math.random().toString()}
                  id={item.id}
                  onMouseEnter={() => handleHover(item.id)}
                  onMouseLeave={handleHoverOut}
                >
                  <div className="card flex justify-content-center">
                    <img
                      alt="post-img"
                      src={item.src}
                      className="post-img img-fluid"
                    />

                    <div
                      id={item.id}
                      className={`row fs-5 update-action-block ${
                        hoveredItemId === item.id ? "" : ""
                      }`}
                    >
                      <OverlayTrigger
                        key={item.name + Math.random().toString()}
                        placement="top"
                        delay={{ show: 50, hide: 50 }}
                        overlay={renderTooltip("Download")}
                      >
                        <a
                          href="/"
                          onClick={(e) => downloadItem(e, item)}
                          className="col-md-6 col-sm-12   text-start px-3  text-center"
                        >
                          <i className="col bi bi-download"></i>
                        </a>
                      </OverlayTrigger>
                      <OverlayTrigger
                        key={item.name + Math.random().toString()}
                        placement="top"
                        delay={{ show: 50, hide: 50 }}
                        overlay={renderTooltip(
                          isExisting || isIncluded ? existInCart : "Add to Cart"
                        )}
                      >
                        <a
                          href="/"
                          aria-disabled={isIncluded ? true : false}
                          onClick={(e) => {
                            if (
                              e.currentTarget.getAttribute("aria-disabled") ===
                              "true"
                            ) {
                              e.preventDefault();
                              return;
                            } else {
                              addItem(e, item);
                            }
                          }}
                          className={
                            `col-md-6 col-sm-12 text-start px-3  text-center ` +
                            (isIncluded ? "disabled-link" : " ")
                          }
                        >
                          <i className="col bi bi-cart"></i>
                        </a>
                      </OverlayTrigger>
                    </div>
                  </div>
                  <div className="text-center my-3 fw-bold">
                    <span>{title}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};
